import React, { useEffect, useMemo, useState, useRef } from 'react';
import './CallWindow.css';
import { formatPhoneDisplay, formatPhoneInput, normalizePhoneDigits } from '../../utils/phone';
import type { TelephonyToastState } from '../../types/telephony.events';
import { crmPhone } from '../../services/crmphone.service';
import Modal from '../Modal/Modal';
// import AddCustomerForm, { CustomerFormData } from '../Customers/AddCustomerForm';
import customerService from '../../services/customer.service';
import dictsService, { DictItem } from '../../services/dicts.service';
import TicketCreateModal from '../Tickets/TicketCreateModal';
import TicketLinkCustomerModal from '../Tickets/TicketLinkCustomerModal';
import { useDicts } from '../../contexts/DictsContext';
import { useAppFeatures } from '../../contexts/AppFeaturesContext';
import actionAdminService from '../../services/action-admin.service';
import type { Action } from '../../types/action-admin.types';
import actionsService from '../../services/actions.service';
import { Ticket, PhoneIncoming, PhoneOutgoing, Minimize2, User, Search, X } from 'lucide-react';
import { useTelephonyAgentEvents, CallLocalState } from '../../contexts/TelephonyAgentEventsContext';
import { showToast } from '../../utils/toast';

const STAGE_META: Record<string, { label: string; color: string }> = {
  dialing: { label: 'Набор', color: 'var(--success-500)' },
  ring: { label: 'Звонит', color: 'var(--warning-500)' },
  answered: { label: 'Отвечен', color: 'var(--accent-500)' },
  bridge: { label: 'Соединён', color: 'var(--accent-500)' },
  hangup: { label: 'Завершён', color: 'var(--gray-500)' },
  transfer: { label: 'Переведён', color: 'var(--accent-700)' },
  hold: { label: 'Удержание', color: 'var(--warning-400)' },
  missed: { label: 'Пропущен', color: 'var(--error-500)' },
  failed: { label: 'Не дозвонились', color: 'var(--error-500)' },
  queued: { label: 'В очереди', color: 'var(--accent-400)' },
};

const resolveStageMeta = (event?: string | null) => {
  if (!event) return null;
  const stage = String(event).toLowerCase();
  if (STAGE_META[stage]) return { stage, ...STAGE_META[stage] };
  const normalized = stage.replace(/[_-]+/g, ' ');
  return {
    stage,
    label: normalized.charAt(0).toUpperCase() + normalized.slice(1),
    color: 'var(--gray-400)',
  };
};

const getDirectionLabel = (direction: TelephonyToastState['direction']) => {
  if (direction === 'inbound') return 'Входящий';
  if (direction === 'outbound') return 'Исходящий';
  if (direction === 'dialer') return 'Автодозвон';
  return 'Звонок';
};

interface CallWindowProps {
  call: TelephonyToastState;
  style?: React.CSSProperties;
  state: CallLocalState;
  onStateChange: (patch: Partial<CallLocalState>) => void;
  tabs?: React.ReactNode;
}

const CallWindow: React.FC<CallWindowProps> = ({ call, style, state, onStateChange, tabs }) => {
  const { dicts, ensureLoaded: ensureDictsLoaded } = useDicts();
  const phonePretty = useMemo(() => formatPhoneDisplay(call.phone || ''), [call.phone]);
  const stageMeta = useMemo(() => resolveStageMeta(call.telephonyEvent), [call.telephonyEvent]);
  const { tickets: ticketsFeature } = useAppFeatures();
  const [searching] = useState(false);
  const [foundCustomer, setFoundCustomer] = useState<{ id: number; title: string } | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [ticketOpen, setTicketOpen] = useState(false);
  // const [creating, setCreating] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [showCandidates, setShowCandidates] = useState<boolean>(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [suspended, setSuspended] = useState(false);
  // Поиск клиентов на сервере (ручной запуск)
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [searchValue, setSearchValue] = useState('');
  const [serverLoading, setServerLoading] = useState(false);
  const [serverItems, setServerItems] = useState<any[]>([]);
  const [serverMode, setServerMode] = useState(false);
  const [qcFullName, setQcFullName] = useState('');
  const [qcPhone, setQcPhone] = useState(phonePretty || '');
  const [qcEmail, setQcEmail] = useState('');
  const [qcLoading, setQcLoading] = useState(false);
  const [qcIin, setQcIin] = useState('');
  const [qcEmailType, setQcEmailType] = useState('');
  const [emailTypes, setEmailTypes] = useState<DictItem[]>([]);
  const [qcPhoneType, setQcPhoneType] = useState('');
  const [phoneTypes, setPhoneTypes] = useState<DictItem[]>([]);
  const [callResultItems, setCallResultItems] = useState<Action[]>([]);
  const callResult = state.resultCode ?? '';
  const callComment = state.comment ?? '';

  const canAnswer = call.direction === 'inbound';
  const { docked, chooseCustomer, minimize, setPanelUserHidden, close } = useTelephonyAgentEvents();

  const hasFull = Boolean(call.customerFullName && String(call.customerFullName).trim());
  const hasIin = Boolean(call.customerIin && String(call.customerIin).trim());
  const showFullAndIin = hasFull && hasIin;
  const hasCandidates = Array.isArray(call.identifyCandidates) && call.identifyCandidates.length > 0;
  const candidates = useMemo(() => Array.isArray(call.identifyCandidates) ? call.identifyCandidates : [], [call.identifyCandidates]);
  const toneRef = useRef<{ ctx: AudioContext; osc: OscillatorNode; gain: GainNode; timer: number } | null>(null);

  const stopDialTone = React.useCallback(() => {
    const current = toneRef.current;
    if (!current) return;
    window.clearInterval(current.timer);
    try {
      const { ctx, gain, osc } = current;
      const now = ctx.currentTime;
      gain.gain.cancelScheduledValues(now);
      gain.gain.setValueAtTime(gain.gain.value, now);
      gain.gain.linearRampToValueAtTime(0.0001, now + 0.1);
      osc.stop(now + 0.15);
      window.setTimeout(() => {
        try { osc.disconnect(); } catch (error) { void error; }
        try { gain.disconnect(); } catch (error) { void error; }
        try { ctx.close(); } catch (error) { void error; }
      }, 250);
    } catch (error) { void error; }
    toneRef.current = null;
  }, []);

  // startDialTone удалён — звук исходящего вызова отключён

  const runServerSearch = async () => {
    const q = (searchInputRef.current?.value || '').trim();
    setSearchValue(q); // Сохраняем значение поиска
    if (!q) { setServerMode(false); setServerItems([]); return; }
    try {
      setServerLoading(true);
      const res = await customerService.getCustomers({ search: q, limit: 100, skip: 0 });
      const items = Array.isArray(res.items) ? res.items : [];
      const mapped = items.map((it: any) => ({
        id: Number(it.id),
        fullName: it.full_name || it.name || `${it.first_name || ''} ${it.last_name || ''}`.trim(),
        iin: it.identification_no || it.iin || undefined,
        phones: Array.isArray(it.phones)
          ? it.phones.map((p: any) => p.phone_number || p.number || '')
          : (it.phone_number ? [it.phone_number] : []),
      }));
      setServerItems(mapped);
      setServerMode(true);
    } catch {
      setServerItems([]);
      setServerMode(true);
    } finally { setServerLoading(false); }
  };

  // Инициализация видимости выбора кандидатов
  useEffect(() => {
    if (hasCandidates && !call.selectedCustomerId) {
      setShowCandidates(true);
    }
  }, [hasCandidates, call.selectedCustomerId]);

  // Горячие клавиши: Enter — ответить, Esc — завершить
  useEffect(() => {
    if (call.minimized) return;
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const tag = (target?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || (target as any)?.isContentEditable) {
        return; // не перехватываем, если фокус в поле ввода
      }
      if (e.key === 'Enter' && canAnswer) { e.preventDefault(); crmPhone.answer(); }
      if (e.key === 'Escape') { e.preventDefault(); crmPhone.hangup(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [canAnswer, call.minimized]);

  // Отключаем тон набора для исходящих вызовов (по требованию)
  useEffect(() => {
    stopDialTone();
    return () => { stopDialTone(); };
  }, [call.direction, stageMeta, stopDialTone]);

  // Убрали автопоиск клиента по номеру — бэкенд присылает нужные данные/кандидатов

  // Полная форма создания клиента удалена — используем мини‑форму (quickCreate)

  const startTicketCreation = () => {
    void ensureDictsLoaded();
    setTicketOpen(true);
  };

  // legacy modals left in codebase (not used in new aside UX)

  const quickCreate = async () => {
    try {
      setErr(null);
      setQcLoading(true);
      const name = (qcFullName || '').trim();
      const iinDigits = (qcIin || '').replace(/\D/g, '');
      const phoneDigits = (qcPhone || '').replace(/\D/g, '');
      if (!name) { setErr('Укажите ФИО'); setQcLoading(false); return; }
      // ИИН необязателен, но если указан - должен содержать 12 цифр
      if (iinDigits && iinDigits.length !== 12) { setErr('ИИН должен содержать 12 цифр'); setQcLoading(false); return; }
      if (phoneDigits.length < 10) { setErr('Введите корректный номер телефона'); setQcLoading(false); return; }
      const payload: any = {
        full_name: name,
        phones: [{ number: normalizePhoneDigits(qcPhone) || qcPhone, type_code: qcPhoneType || 'mobile' }],
      };
      // Добавляем emails только если указан email
      if (qcEmail.trim()) {
        payload.emails = [{ email: qcEmail.trim(), type_code: qcEmailType || 'personal' }];
      }
      // Добавляем identification_no только если указан ИИН
      if (iinDigits) {
        payload.identification_no = iinDigits;
      }
      const created = await customerService.createCustomer(payload);
      try {
        chooseCustomer(call.callId, {
          id: created.id,
          fullName: (created.full_name || created.display_name || `${created.first_name || ''} ${created.last_name || ''}`).trim(),
          iin: created.identification_no || undefined,
        } as any);
        setShowCandidates(false);
        // Поле поиска удалено
        setFoundCustomer({ id: created.id, title: created.full_name || created.display_name || `Клиент #${created.id}` });
        setCreateOpen(false);
      } catch (error) {
        void error;
      }
    } catch (e: any) {
      setErr(e?.message || 'Не удалось создать клиента');
    } finally { setQcLoading(false); }
  };

  const dirLabel = getDirectionLabel(call.direction);

  const onSaveAndClose = async () => {
    const resultCode = callResult;
    if (!resultCode) { showToast('Выберите результат звонка', 'error'); return; }
    const subjectId = call.selectedCustomerId ?? call.customerId;
    try {
      const candidatesPayload = Array.isArray(call.identifyCandidates)
        ? (call.identifyCandidates as any[]).map((c: any) => ({
            id: Number(c.id),
            fullName: c.fullName || c.full_name || c.name || undefined,
            iin: c.iin ?? undefined,
            phones: Array.isArray(c.phones) ? c.phones : undefined,
          }))
        : undefined;
      await actionsService.addHistory(resultCode, {
        subject_type: 'customer',
        subject_id: subjectId ?? null,
        comment: callComment || '',
        call_id: call.callId,
        direction: call.direction ?? '',
        payload: {
          ...(candidatesPayload && { identify_candidates: candidatesPayload }),
          ...(call.recordingUrl && { recording_url: call.recordingUrl }),
          ...(call.recordingReady !== undefined && { recording_ready: call.recordingReady }),
        },
      });
      showToast('Сохранено', 'success');
      try { close(call.callId); } catch (error) { void error; }
    } catch (e: any) {
      showToast(e?.message || 'Не удалось сохранить результат', 'error');
    }
  };

  const CustomerInfo = () => (
    <div className="cw-section">
      <div className="cw-row mb-6px">
        <div className="cw-customer">
          {showFullAndIin ? (
            <>
              <span>Клиент: {call.customerFullName}</span>
              <span className="cw-customer__sub">ИИН: {call.customerIin}</span>
            </>
          ) : hasFull ? (
            <span>Клиент: {call.customerFullName}</span>
          ) : hasIin ? (
            <span>ИИН: {call.customerIin}</span>
          ) : call.customerId ? (
            <span>Клиент: #{call.customerId}</span>
          ) : searching ? (
            <span className="cw-skeleton" aria-hidden></span>
          ) : foundCustomer ? (
            <span>Клиент: {foundCustomer.title}</span>
          ) : (
            <span>Клиент не найден</span>
          )}
        </div>
        {!!call.customerId && !showCandidates && (
          <button className="cw-link" onClick={() => setShowCandidates(true)}>Сменить клиента</button>
        )}
      </div>
      <div className="cw-meta">
        <div className="cw-meta__item"><span className="cw-meta__label">Телефон</span><span className="cw-meta__value">{phonePretty || '—'}</span></div>
        <div className="cw-meta__item">
          <span className="cw-meta__label">Направление</span>
          <span
            className="cw-meta__value"
            style={stageMeta ? ({ '--stage-color': stageMeta.color } as React.CSSProperties) : undefined}
          >
            {dirLabel}{stageMeta ? ` · ${stageMeta.label}` : ''}
          </span>
        </div>
        <div className="cw-meta__item"><span className="cw-meta__label">Время</span><span className="cw-meta__value">сейчас</span></div>
      </div>
      {/* Блок быстродействий (ответ/завершить) удалён по требованиям */}
      {ticketsFeature && !!call.selectedCustomerId && (
        <div className="cw-chips">
          <button className="cw-chip" onClick={startTicketCreation} title="Создать тикет">
            <Ticket size={16} />
            <span>Создать тикет</span>
          </button>
        </div>
      )}
    </div>
  );

  const UnknownLayout = () => (
    <div className="cw-section ux-content">
      <div className="ux-header">
        <div className="ux-title"><User size={20} /> Неизвестный абонент</div>
        <div>
          <button className="btn-link-primary" onClick={() => setCreateOpen(true)}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M12 5v14M5 12h14"/></svg>
            Создать клиента
          </button>
        </div>
      </div>
      <div className="ux-searchrow">
        <div className="ux-searchwrap">
          <div className="ux-searchicon"><Search size={16} /></div>
          <input
            ref={searchInputRef}
            className="ux-search"
            placeholder="Поиск: имя, телефон, email, ИИН"
            defaultValue={searchValue}
          />
        </div>
        <button className="btn-link-primary" onClick={runServerSearch} disabled={serverLoading}>
          {serverLoading ? 'Идёт поиск…' : 'Поиск'}
        </button>
        {serverMode && (
          <button className="btn-ghost" onClick={() => {
            setServerMode(false);
            setServerItems([]);
            setSearchValue('');
            if (searchInputRef.current) searchInputRef.current.value = '';
          }}>
            Очистить
          </button>
        )}
      </div>
      <div className="ux-scroll">
        <div className="cand-grid">
          {(serverMode ? serverItems : candidates).map((cand) => {
            const title = String(cand.fullName || cand.full_name || cand.name || `Клиент #${cand.id}`);
            const iin = cand.iin ? String(cand.iin) : '';
            const phone = Array.isArray(cand.phones) ? String(cand.phones[0] || '') : '';
            return (
              <div key={cand.id} className="cand-card">
                <div className="cand-head">
                  <div className="cand-name">{title}</div>
                </div>
                <div className="cand-info">
                  {phone && <span>{formatPhoneDisplay(phone)}</span>}
                  {phone && <span>·</span>}
                  {iin && <span>ИИН: {iin}</span>}
                </div>
                <div className="match">
                  <div className="match-label">Совпадение</div>
                  <div className="match-bar"><div className="match-bar__fill" style={{ ['--pct' as any]: '100%' }} /></div>
                  <div className="text-gray-700 text-12" style={{ justifySelf: 'end' }}>100%</div>
                </div>
                <div className="cand-actions">
                  <button className="btn-link-primary" onClick={() => { chooseCustomer(call.callId, cand); setShowCandidates(false); }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M10 13a5 5 0 0 0 7 0l1-1a5 5 0 1 0-7-7l-1 1"/><path d="M14 11a5 5 0 0 0-7 0l-1 1a5 5 0 1 0 7 7l1-1"/></svg>
                    Привязать
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {/* ux-footnote удалён по требованиям */}
    </div>
  );

  const containerRef = React.useRef<HTMLDivElement>(null);
  // Поле поиска удалено

  // Убираем форс‑фокус окна, чтобы не терялся курсор в полях ввода
  useEffect(() => {
    const onVis = () => { /* не меняем фокус */ };
    window.addEventListener('crmphone:visibility', onVis as any);
    return () => window.removeEventListener('crmphone:visibility', onVis as any);
  }, []);

  const needAside = (!call.customerId) || showCandidates;

  // Загружаем значения справочника "Результат звонка"
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const items = await actionAdminService.listActions({
          group_code: 'call_results',
          include_hidden: false,
          include_deleted: false,
        });
        if (!cancelled) setCallResultItems(items);
      } catch {
        if (!cancelled) setCallResultItems([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Загружаем типы email и телефона при открытии модалки создания клиента
  useEffect(() => {
    if (!createOpen) return;
    let cancelled = false;
    (async () => {
      try {
        const [emailItems, phoneItems] = await Promise.all([
          dictsService.listDictItems('email_types'),
          dictsService.listDictItems('phone_types'),
        ]);
        if (!cancelled) {
          setEmailTypes(emailItems);
          setPhoneTypes(phoneItems);
          // Устанавливаем первый тип по умолчанию если список не пустой
          if (emailItems.length > 0 && !qcEmailType) {
            setQcEmailType(emailItems[0].code);
          }
          if (phoneItems.length > 0 && !qcPhoneType) {
            setQcPhoneType(phoneItems[0].code);
          }
        }
      } catch {
        if (!cancelled) {
          setEmailTypes([]);
          setPhoneTypes([]);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [createOpen]);

  return (
    <div
      ref={containerRef}
      tabIndex={-1}
      className={`call-window ${docked ? 'call-window--docked' : ''} ${call.minimized ? 'call-window--minimized' : ''} ${suspended ? 'call-window--collapsed' : ''}`}
      style={style}
    >
      {tabs}
      <div className="call-window__header">
        <div className="call-window__title">
          <span
            className="cw-dir"
            style={stageMeta ? ({ '--stage-color': stageMeta.color } as React.CSSProperties) : undefined}
          >
            {call.direction === 'inbound' ? (
              <PhoneIncoming size={14} aria-hidden />
            ) : call.direction === 'outbound' || call.direction === 'dialer' ? (
              <PhoneOutgoing size={14} aria-hidden />
            ) : null}
            {dirLabel}
            {stageMeta && <span className="cw-dir__stage">{stageMeta.label}</span>}
          </span>
          <span className="cw-phone">{phonePretty}</span>
        </div>
        <div className="call-window__actions">
          {!call.minimized && (
            <button
              className="btn-ghost d-inline-flex items-center gap-6px"
              title="Скрыть телефон"
              onClick={() => {
                try { setPanelUserHidden(true); } catch (error) { void error; }
                try { minimize(call.callId); } catch (error) { void error; }
                try { crmPhone.hide(); } catch (error) { void error; }
                try { localStorage.setItem('crmphone.panelVisible', 'false'); } catch (error) { void error; }
                try { sessionStorage.setItem('crmphone.widgetActive', 'false'); } catch (error) { void error; }
                try { window.dispatchEvent(new CustomEvent('crmphone:visibility', { detail: { visible: false } })); } catch (error) { void error; }
              }}
            >
              <Minimize2 size={16} />
              <span>Скрыть</span>
            </button>
          )}
        </div>
      </div>

      {/* Content */}
          <div className={`call-window__body`}>
          <div className="cw-outcome">
            <div className="cw-outcome__control">
              <label className="form-label">Результат звонка</label>
              <select
                className="input"
                value={callResult}
                onChange={(e) => onStateChange({ resultCode: e.target.value })}
              >
                <option value="">Выберите результат</option>
                {callResultItems.map((it) => (
                  <option key={it.id} value={it.code}>{it.name}</option>
                ))}
              </select>
            </div>
            <div className="form-actions">
              <button className="btn-primary" onClick={onSaveAndClose}>Сохранить и закрыть</button>
            </div>
          </div>
          <div className="cw-comment">
            <div className="form-group">
              <label className="form-label">Комментарий</label>
              <textarea
                className="input"
                placeholder="Комментарий"
                rows={3}
                value={callComment}
                onChange={(e) => onStateChange({ comment: e.target.value })}
              />
            </div>
          </div>
          <div className={`cw-pane`}>
            <div className={`cw-grid`}>
              <div className="cw-main">
                <div className="cw-card">
                  {needAside ? <UnknownLayout /> : (<>
                    <div className="cw-card__title">Информация</div>
                    <CustomerInfo />
                  </>)}
                </div>
              </div>
            </div>
          </div>
      </div>

      {/* Модалки */}
      <Modal isOpen={createOpen} onClose={() => setCreateOpen(false)} title="Быстрое создание" size="sm">
        {err && (
          <div className="alert alert-error mb-8px">
            <span>{err}</span>
          </div>
        )}
        <div className="form-group"><label className="form-label">Имя <span className="text-error-500">*</span></label><input className="input" placeholder="Имя клиента" value={qcFullName} onChange={(e) => setQcFullName(e.target.value)} /></div>
        <div className="form-group"><label className="form-label">ИИН</label><input className="input" placeholder="12 цифр (необязательно)" value={qcIin} onChange={(e) => setQcIin(e.target.value.replace(/\D/g, '').slice(0, 12))} /></div>
        <div className="form-group">
          <label className="form-label">Email</label>
          <div className="flex-g8">
            <input className="input flex-1" placeholder="name@example.com (необязательно)" value={qcEmail} onChange={(e) => setQcEmail(e.target.value)} />
            <select className="input w-140px" value={qcEmailType} onChange={(e) => setQcEmailType(e.target.value)}>
              {emailTypes.length === 0 && <option value="">—</option>}
              {emailTypes.map((t) => (
                <option key={t.id} value={t.code}>{t.name}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Телефон <span className="text-error-500">*</span></label>
          <div className="flex-g8">
            <input className="input flex-1" placeholder="+7 707 111-22-33" value={qcPhone} onChange={(e) => setQcPhone(formatPhoneInput(e.target.value))} />
            <select className="input w-140px" value={qcPhoneType} onChange={(e) => setQcPhoneType(e.target.value)}>
              {phoneTypes.length === 0 && <option value="">—</option>}
              {phoneTypes.map((t) => (
                <option key={t.id} value={t.code}>{t.name}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="flex gap-8px justify-end" style={{ marginTop: 10 }}>
          <button className="btn-ghost" onClick={() => setCreateOpen(false)}>Отмена</button>
          <button className="btn-link-primary" onClick={quickCreate} disabled={qcLoading}>
            {qcLoading ? 'Создание…' : 'Создать и привязать'}
          </button>
        </div>
      </Modal>

      <TicketCreateModal
        isOpen={ticketOpen}
        onClose={() => setTicketOpen(false)}
        dicts={dicts}
        onCreated={() => {
          setTicketOpen(false);
          try { window.dispatchEvent(new Event('tickets:changed' as any)); } catch (error) { void error; }
        }}
      />

      <TicketLinkCustomerModal
        isOpen={linkOpen}
        onClose={() => { setLinkOpen(false); setSuspended(false); }}
        onSubmit={(c) => {
          try {
            chooseCustomer(call.callId, { id: c.id, fullName: c.full_name || undefined, iin: c.identification_no || undefined } as any);
            setShowCandidates(false);
            setFoundCustomer({ id: c.id, title: c.full_name || `Клиент #${c.id}` });
          } catch { /* noop */ }
          setLinkOpen(false);
          setSuspended(false);
        }}
      />
    </div>
  );
};

export default CallWindow;

// Root-контейнер: подписывается на контекст и размещает CallWindow в доке/оверлее.
export const CallWindowRoot: React.FC = () => {
  const {
    toasts,
    activeCallId,
    setActiveCall,
    getCallState,
    updateCallState,
    docked,
    restore,
    close,
  } = useTelephonyAgentEvents();
  const [dockOffset, setDockOffset] = useState<number>(16);
  const [dockWidth, setDockWidth] = useState<number>(380);
  const [dockRight, setDockRight] = useState<number>(16);
  const [hasPanel, setHasPanel] = useState<boolean>(false);
  const [viewportWidth, setViewportWidth] = useState<number>(typeof window !== 'undefined' ? window.innerWidth : 1200);
  const [viewportHeight, setViewportHeight] = useState<number>(typeof window !== 'undefined' ? window.innerHeight : 800);

  const active = useMemo(() => {
    if (!toasts.length) return null;
    const byId = activeCallId ? toasts.find((t) => t.callId === activeCallId) : null;
    return byId ?? toasts[0];
  }, [toasts, activeCallId]);
  const showWindow = Boolean(active && !active.minimized);
  const tabsScrollable = toasts.length > 3;

  // Форсированная переоценка размеров панели виджета (на случай динамической высоты
  // и чтобы корректно вернуть карточку после сворачивания/восстановления)
  const measureNow = React.useCallback(() => {
    try {
      const el = crmPhone.getWidgetPanelEl?.();
      if (!el) {
        setHasPanel(false);
        setDockOffset(16);
        setDockWidth(380);
        setDockRight(16);
        return;
      }
      const rect = el.getBoundingClientRect();
      const bottom = 16 + Math.max(0, Math.round(rect.height));
      setDockOffset(bottom);
      const width = Math.max(280, Math.round(rect.width));
      setDockWidth(width);
      const right = Math.max(8, Math.round(window.innerWidth - rect.right));
      setDockRight(right);
      setHasPanel(true);
    } catch { /* noop */ }
  }, []);

  React.useEffect(() => {
    if (!docked) { setDockOffset(16); setHasPanel(false); return; }
    let cancelled = false;
    const measure = () => {
      try {
        const el = crmPhone.getWidgetPanelEl?.();
        if (!el) {
          if (!cancelled) { setDockOffset(16); setDockWidth(380); setDockRight(16); setHasPanel(false); }
          return;
        }
        const rect = el.getBoundingClientRect();
        const bottom = 16 + Math.max(0, Math.round(rect.height));
        if (!cancelled) setDockOffset(bottom);
        const width = Math.max(280, Math.round(rect.width));
        if (!cancelled) setDockWidth(width);
        const right = Math.max(8, Math.round(window.innerWidth - rect.right));
        if (!cancelled) { setDockRight(right); setHasPanel(true); }
      } catch { /* noop */ }
    };
    measure();
    const RO = (window as any).ResizeObserver as any;
    const ro = RO ? new RO(() => measure()) : null;
    if (ro) { try { ro.observe(document.documentElement); } catch (error) { void error; } }
    const mo = new MutationObserver(() => measure());
    mo.observe(document.documentElement, { childList: true, subtree: true, attributes: true });
    const tm = window.setInterval(measure, 500);
    return () => {
      cancelled = true;
      try { mo.disconnect(); } catch (error) { void error; }
      try {
        if (ro) ro.disconnect();
      } catch (error) {
        void error;
      }
      window.clearInterval(tm);
    };
  }, [docked]);

  // При восстановлении окна форсируем несколько измерений (в т.ч. после рендера виджета)
  React.useEffect(() => {
    if (docked && showWindow) {
      measureNow();
      const t1 = window.setTimeout(measureNow, 100);
      const t2 = window.setTimeout(measureNow, 400);
      return () => { window.clearTimeout(t1); window.clearTimeout(t2); };
    }
  }, [docked, showWindow, measureNow]);

  // Следим за размером вьюпорта, чтобы расширять окно влево от правого края
  React.useEffect(() => {
    const onResize = () => { setViewportWidth(window.innerWidth); setViewportHeight(window.innerHeight); };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const renderTabs = () => {
    if (!toasts.length) return null;
    return (
      <div className={`call-tabs ${tabsScrollable ? 'call-tabs--scrollable' : ''}`}>
        <div className="call-tabs__inner">
          {toasts.map((toast) => {
            const stage = resolveStageMeta(toast.telephonyEvent);
            const isActive = active?.callId === toast.callId;
            const dirLabel = getDirectionLabel(toast.direction);
            const label = formatPhoneDisplay(toast.phone || '');
            const onSelect = () => {
              setActiveCall(toast.callId);
              if (toast.minimized) restore(toast.callId);
            };
            const onClose = (ev: React.MouseEvent) => {
              ev.stopPropagation();
              ev.preventDefault();
              close(toast.callId);
            };
            const state = getCallState(toast.callId);
            const showNewBadge = !state.seen;
            return (
              <button
                key={toast.callId}
                className={`call-tab ${isActive ? 'call-tab--active' : ''}`}
                onClick={onSelect}
                type="button"
              >
                <span className="call-tab__meta">
                  {toast.direction === 'inbound' ? (
                    <PhoneIncoming size={14} aria-hidden />
                  ) : toast.direction === 'outbound' || toast.direction === 'dialer' ? (
                    <PhoneOutgoing size={14} aria-hidden />
                  ) : null}
                  <span className="call-tab__dir">{dirLabel}</span>
                </span>
                <span className="call-tab__phone">{label}</span>
                {stage && <span className="call-tab__stage" style={{ ['--stage-color' as any]: stage.color }}>{stage.label}</span>}
                {showNewBadge && <span className="call-tab__badge">Новый</span>}
                <span className="call-tab__close" role="button" aria-label="Закрыть" onClick={onClose}>
                  <X size={12} aria-hidden />
                </span>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const tabs = renderTabs();

  if (!active) {
    return tabs ? <div className="call-dock call-dock--empty">{tabs}</div> : null;
  }

  return (
    <>
      {showWindow && !docked && (
        <div className="call-poplayer" aria-live="polite" aria-atomic>
          <div className="call-poplayer__content">
            <CallWindow
              call={active}
              state={getCallState(active.callId)}
              onStateChange={(patch) => updateCallState(active.callId, patch)}
              tabs={tabs}
            />
          </div>
        </div>
      )}

      {active && docked && hasPanel && (
        <div
          className="call-dock-frame fixed pointer-events-none" style={{ right: dockRight, bottom: 16, width: dockWidth, height: Math.max(0, dockOffset - 16 + 12), background: 'var(--surface-0)', borderLeft: '1px solid var(--border-subtle)', borderRight: '1px solid var(--border-subtle)', borderBottom: '1px solid var(--border-subtle)', borderBottomLeftRadius: 12, borderBottomRightRadius: 12, zIndex: 9, boxShadow: '0 10px 40px rgba(0,0,0,0.45)' }}
        />
      )}

      <div className="call-dock" style={{ bottom: docked ? (hasPanel ? dockOffset : 16) : 16, right: hasPanel ? dockRight : 16 }}>
        {showWindow && docked && (
          <CallWindow
            call={active}
            style={{
              // расширяемся влево, при этом не меньше ширины панели
              width: Math.max(
                hasPanel ? dockWidth : 360,
                Math.min(880, Math.max(360, viewportWidth - (hasPanel ? dockRight : 16) - 16))
              ),
              maxHeight: Math.max(360, viewportHeight - (hasPanel ? dockOffset : 16) - 24),
            }}
            state={getCallState(active.callId)}
            onStateChange={(patch) => updateCallState(active.callId, patch)}
            tabs={tabs}
          />
        )}
      </div>

      {/* Мини‑кнопки восстановления отключены: восстановление только через кнопку в сайдбаре */}
    </>
  );
};
